// components
import FilterButton from "../filter-button/filter-button";

// utils
import { getKeysByValue } from "../../utils/object.utils";
import { capitalizeFirstLetter } from "../../utils/string.utils";
import {
  caratData,
  getLabelInDataByValue,
  metalTagsData,
  stoneCutTagsData,
  stoneTypeTagsData,
  colorTagsData,
  styleTagsData,
  settingTagsData,
} from "../../utils/constants.utils";
import { useTranslation } from "react-i18next";
import { getVisibleSelectedTags, toggleStoneCutSelection } from "./filter.logic";

// interface
interface IFilter {
  setFilters: (filters: any) => void;
  filters: IFilters
}

interface IFilters {
  mainStoneSize: {
    [key: string]: boolean;
  };
  stoneCut: {
    [key: string]: boolean;
  };
}

const Filter = ({ setFilters, filters }: IFilter) => {
  const {t} = useTranslation();
  const EXCLUDED_TAGS = ["cad"];

  const updateUrlParams = (filters: IFilters) => {
  const params = new URLSearchParams(window.location.search);

  // Update stoneSize in the URL
  const selectedSizes = getKeysByValue(filters.mainStoneSize, true);
  const allSizesSelected = Object.keys(filters.mainStoneSize).length === selectedSizes.length;

  if (selectedSizes.length > 0 && !allSizesSelected) {
    params.set('stoneSize', selectedSizes.join(','));
  } else {
    params.delete('stoneSize');
  }

  // Update tags in the URL
  const { visibleSelectedTags, allTagsSelected } = getVisibleSelectedTags(filters.stoneCut, EXCLUDED_TAGS);

  if (visibleSelectedTags.length > 0 && !allTagsSelected) {
    params.set('tags', visibleSelectedTags.join(','));
  } else {
    params.delete('tags');
  }

  // Update the URL without reloading the page
  const newUrl = `${window.location.pathname}?${params.toString()}`;
  window.history.replaceState({}, '', newUrl);
}


  const toggleStoneSize = (size = 5) => {
    const sizeKey = size.toString();
    setFilters((prevFilters:any) => {
      const current = prevFilters.mainStoneSize;
      const keys = Object.keys(current);
      const selected = getKeysByValue(current, true);
      const isAllSelected = selected.length === keys.length;

      let nextMainStoneSize;

      if (isAllSelected) {
        nextMainStoneSize = Object.fromEntries(keys.map((key) => [key, key === sizeKey]));
      } else {
        const nextValue = !current[sizeKey];
        if (!nextValue && selected.length === 1 && selected[0] === sizeKey) {
          nextMainStoneSize = Object.fromEntries(keys.map((key) => [key, true]));
        } else {
          nextMainStoneSize = {
            ...current,
            [sizeKey]: nextValue,
          };
        }
      }

      const updatedFilters = {
        ...prevFilters,
        mainStoneSize: nextMainStoneSize,
      };

      updateUrlParams(updatedFilters);
      return updatedFilters;
    });
  };

  const toggleStoneShape = (shape: string) => {
    if (EXCLUDED_TAGS.includes(shape)) return;

    setFilters((prevFilters:any) => {
      const current = prevFilters.stoneCut;
      const nextStoneCut = toggleStoneCutSelection(current, shape, EXCLUDED_TAGS);

      const updatedFilters = {
        ...prevFilters,
        stoneCut: nextStoneCut,
      };

      updateUrlParams(updatedFilters);
      return updatedFilters;
    });
  };
  return (
    <>
      <label>{t('sidebar.Ring type')}</label>
      <div>
        <FilterButton isActive={true}>{t('sidebar.Women')}</FilterButton>
      </div>

      <label>{t('sidebar.Stone size')}</label>
      <div>
        {Object.keys(filters.mainStoneSize).map((caratSize, index) => (
          <FilterButton
            key={index}
            isActive={filters.mainStoneSize[caratSize] === true}
            onClick={() => toggleStoneSize(parseFloat(caratSize))}
          >{`${getLabelInDataByValue(caratData, caratSize)} carat`}</FilterButton>
        ))}
      </div>
      <label>{t('sidebar.Stone cut')}</label>
      <div>
        {stoneCutTagsData.map(({label, value}, index) => (
          <FilterButton
            key={index}
            isActive={filters.stoneCut[value] === true}
            onClick={() => toggleStoneShape(value)}
          >
            {capitalizeFirstLetter(getLabelInDataByValue([...stoneTypeTagsData, ...stoneCutTagsData, ...colorTagsData, ...styleTagsData, ...metalTagsData, ...settingTagsData], value))}
          </FilterButton>
        ))}
      </div>
      <label>{t('sidebar.Stone type')}</label>
      <div>
        {stoneTypeTagsData.map(({label, value}, index) => (
          <FilterButton
            key={index}
            isActive={filters.stoneCut[value] === true}
            onClick={() => toggleStoneShape(value)}
          >
            {capitalizeFirstLetter(getLabelInDataByValue([...stoneTypeTagsData, ...stoneCutTagsData, ...colorTagsData, ...styleTagsData, ...metalTagsData, ...settingTagsData], value))}
          </FilterButton>
        ))}
      </div>
      <label>{t('sidebar.Stone color')}</label>
      <div>
        {colorTagsData.map(({label, value}, index) => (
          <FilterButton
            key={index}
            isActive={filters.stoneCut[value] === true}
            onClick={() => toggleStoneShape(value)}
          >
            {capitalizeFirstLetter(getLabelInDataByValue([...stoneTypeTagsData, ...stoneCutTagsData, ...colorTagsData, ...styleTagsData, ...metalTagsData, ...settingTagsData], value))}
          </FilterButton>
        ))}
      </div>
      <label>{t('sidebar.Setting')}</label>
      <div>
        {settingTagsData.map(({label, value}, index) => (
          <FilterButton
            key={index}
            isActive={filters.stoneCut[value] === true}
            onClick={() => toggleStoneShape(value)}
          >
            {capitalizeFirstLetter(getLabelInDataByValue([...stoneTypeTagsData, ...stoneCutTagsData, ...colorTagsData, ...styleTagsData, ...metalTagsData, ...settingTagsData], value))}
          </FilterButton>
        ))}
      </div>
      <label>{t('sidebar.Style')}</label>
      <div>
        {styleTagsData.map(({label, value}, index) => (
          <FilterButton
            key={index}
            isActive={filters.stoneCut[value] === true}
            onClick={() => toggleStoneShape(value)}
          >
            {capitalizeFirstLetter(getLabelInDataByValue([...stoneTypeTagsData, ...stoneCutTagsData, ...colorTagsData, ...styleTagsData, ...metalTagsData, ...settingTagsData], value))}
          </FilterButton>
        ))}
      </div>
      <label>{t('sidebar.Metal')}</label>
      <div>
        {metalTagsData.map(({label, value}, index) => (
          <FilterButton
            key={index}
            isActive={filters.stoneCut[value] === true}
            onClick={() => toggleStoneShape(value)}
          >
            {capitalizeFirstLetter(getLabelInDataByValue([...stoneTypeTagsData, ...stoneCutTagsData, ...colorTagsData, ...styleTagsData, ...metalTagsData, ...settingTagsData], value))}
          </FilterButton>
        ))}
      </div>
    </>
  );
};

export default Filter;
